import { Component, effect, inject, input, output } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AUTO_ACTIVATION_DEFAULT,
  CryptoTokenField,
  cryptoTokenFormValueToDto,
} from './crypto-token-form.types';
import { CryptoTokenType } from 'src/app/shared/entities/crypto-token-type';
import { FormFieldService } from 'src/app/shared/form-field/services/form-field/form-field.service';
import { FormFieldComponent } from 'src/app/shared/form-field/components/form-field/form-field.component';
import { FormFieldsDirective } from 'src/app/shared/form-field/directives/form-fields/form-fields.directive';
import { FormFieldOptions } from 'src/app/shared/form-field/form-field.types';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import {
  outputFromObservable,
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { combineLatestWith, filter, map, merge, startWith, tap } from 'rxjs';
import { CreateCryptoTokenRequestDto } from 'src/app/shared/services/api/dto/create-crypto-token-request-dto';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import { MatButtonModule } from '@angular/material/button';
import { CardComponent } from 'src/app/shared/components/card/card.component';

@Component({
  selector: 'app-crypto-token-form',
  imports: [
    ReactiveFormsModule,
    FormFieldComponent,
    FormFieldsDirective,
    SpinnerComponent,
    MatButtonModule,
    CardComponent,
  ],
  providers: [FormFieldService],
  templateUrl: './crypto-token-form.component.html',
  styleUrl: './crypto-token-form.component.scss',
})
export class CryptoTokenFormComponent {
  slots = input<string[]>();
  areSlotsLoading = input<boolean>();
  cryptotokenRequest = output<CreateCryptoTokenRequestDto>();
  cancelCreation = output();

  private lang = inject(LangService).lang;
  labels = this.lang.newCryptotoken;
  private dialogService = inject(DialogService);
  private fb = inject(FormBuilder);
  private formFieldService = inject(FormFieldService);
  form = this.fb.group({
    [CryptoTokenField.secret]: new FormControl('', [Validators.required]),
    [CryptoTokenField.tokenName]: new FormControl('', [
      Validators.required,
      Validators.pattern(ValidationConstants.cryptotoken.namePattern),
    ]),
    [CryptoTokenField.tokenType]: new FormControl(CryptoTokenType.SOFT),
    [CryptoTokenField.autoActivation]: new FormControl(AUTO_ACTIVATION_DEFAULT),
    [CryptoTokenField.slotLabel]: new FormControl(''),
  });

  private isPKCS11$ = this.form.controls[
    CryptoTokenField.tokenType
  ].valueChanges.pipe(map(value => value === CryptoTokenType.PKCS11));

  isPKCS11 = toSignal(this.isPKCS11$);

  needSlots = outputFromObservable(
    this.isPKCS11$.pipe(
      combineLatestWith(toObservable(this.slots)),
      filter(([isPKCS11, slots]) => isPKCS11 && !slots),
      map(() => {
        /* emit void signal */
      }),
    ),
  );

  CryptoTokenField = CryptoTokenField;

  // Whenever new slots are available, the option list for the Slot Label dropdown must be reconfigured.
  private updateSlotsEffect = () => {
    const slots = this.slots();
    this.formFieldService.updateControlLabels(CryptoTokenField.slotLabel, {
      options: slots,
    });
  };

  onSubmit() {
    this.form.markAllAsTouched(); // triggers validation
    if (!this.form.valid) return;
    const dto = cryptoTokenFormValueToDto(this.form.value);
    this.cryptotokenRequest.emit(dto);
  }

  onCancel() {
    this.dialogService.withConfirmation({
      text: this.labels.cancelWarning,
      confirmIf: () => this.form.dirty,
    })(() => this.cancelCreation.emit());
  }

  constructor() {
    effect(this.updateSlotsEffect);
    this.formFieldService.configure({
      labels: this.labels.inputs,
      options: {
        [CryptoTokenField.secret]: FormFieldOptions.text({
          required: true,
        }),
        [CryptoTokenField.tokenType]: FormFieldOptions.select({
          required: true,
        }),
        [CryptoTokenField.tokenName]: FormFieldOptions.text({ required: true }),
        [CryptoTokenField.autoActivation]: FormFieldOptions.checkbox(),
        [CryptoTokenField.slotLabel]: FormFieldOptions.select({
          useEmpty: true,
        }),
      },
    });
    merge(this.updateFormLabels).pipe(takeUntilDestroyed()).subscribe();
  }

  private autoActivatedSecretLabels = this.labels.inputs.secretName;
  private nonAutoActivatedSecretLabels = this.labels.inputs.password;

  private updateFormLabels = this.form.controls[
    CryptoTokenField.autoActivation
  ].valueChanges.pipe(
    startWith(AUTO_ACTIVATION_DEFAULT),
    map(isAutoActivated =>
      isAutoActivated
        ? this.autoActivatedSecretLabels
        : this.nonAutoActivatedSecretLabels,
    ),
    tap(labels =>
      this.formFieldService.updateControlLabels(
        CryptoTokenField.secret,
        labels,
      ),
    ),
  );
}
